import { useState, useEffect } from "react";
import { Clock, Lock, Check } from "lucide-react";
import { authFetch } from "../../../api";

export default function SAP_EnrolledPrograms() {
  const userId = localStorage.getItem("userId");
  const [openId, setOpenId] = useState(null);
  const [programs, setPrograms] = useState([]);

  const fetchEnrolledPrograms = async () => {
    try {
      const res = await authFetch("packages/get-enrolled-program-details", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });

      const data = await res.json();
      setPrograms(data.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchEnrolledPrograms();
  }, []);

  return (
    <div className="max-w-6xl mx-auto p-6 font-inter">
      <h2 className="text-xl font-semibold mb-6">
        Enrolled <span className="text-[#572575]">Programs</span>
      </h2>

      <div className="space-y-6">
        {programs.map((program) => {
          const isOpen = openId === program.enrollment_id;
          const hasInstallments =
            program.installments && program.installments.length > 0;

          const formattedDate = program.start_date
            ? new Date(program.start_date).toLocaleDateString()
            : "N/A";

          const statusText = program.status === 1 ? "Active" : "Inactive";

          return (
            <div
              key={program.enrollment_id}
              className="rounded-2xl border border-[#E9E9F0] bg-gradient-to-r from-white to-[#FBF5FF]"
            >
              {/* HEADER */}
              <div className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex gap-4">
                    <div className="h-12 w-12 rounded-xl border border-[#E9D7F7] bg-white flex items-center justify-center">
                      <img
                        src="/EN.png"
                        alt="Program Icon"
                        className="h-7 w-7"
                      />
                    </div>

                    <div>
                      <h3 className="text-[16px] font-semibold text-[#1C1C1C]">
                        {program.program_name}
                      </h3>
                      <p className="mt-1 text-[13px] text-[#5F6368]">
                        {program.program_description ||
                          "No description available"}
                      </p>
                    </div>
                  </div>

                  <div className="text-[16px] font-semibold text-[#6A1B9A]">
                    {program.currency} {program.price}
                  </div>
                </div>

                {/* META */}
                <div className="mt-5 flex items-center gap-8">
                  <MetaItem
                    icon={<img src="/active.png" className="h-6 w-6" />}
                    value={statusText}
                    label="Status"
                  />

                  <MetaItem
                    icon={<img src="/time.png" className="h-6 w-6" />}
                    value={program.duration || "N/A"}
                    label="Duration"
                  />

                  <MetaItem
                    icon={<img src="/calender.png" className="h-6 w-6" />}
                    value={formattedDate}
                    label="Start Date"
                  />
                </div>
              </div>

              <div className="h-[1px] bg-[#E6E6E6] mx-6" />

              {/* PERKS HEADER */}
              <div className="px-6 py-4 flex justify-between items-center">
                <p className="flex items-center gap-2 text-[14px] font-medium text-[#4A148C]">
                  <img src="/gift.png" className="h-4 w-4" />
                  Perks Of Program
                </p>

                <button
                  onClick={() =>
                    setOpenId(isOpen ? null : program.enrollment_id)
                  }
                  className="flex items-center gap-1 text-[14px] font-medium text-[#2979FF]"
                >
                  {isOpen ? "View Less" : "View More"}
                  <span className="text-xs">{isOpen ? "▲" : "▼"}</span>
                </button>
              </div>

              {isOpen && (
                <div className="px-6 pb-6 space-y-6">
                  {/* PERKS */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    {[
                      "Personalized Profile Evaluation",
                      "University Shortlisting",
                      "Test Preparation",
                      "Application Strategy & Timeline",
                      "SOP & Essay Support",
                      "CV/Resume Optimization",
                      "LOR Guidance",
                      "Scholarship & Financial Aid",
                      "Mock Interviews & Video Essays",
                    ].map((perk) => (
                      <div
                        key={perk}
                        className="flex items-center gap-2 text-gray-700"
                      >
                        <span className="text-[#572575]">✔</span>
                        {perk}
                      </div>
                    ))}
                  </div>

                  {/* PAYMENTS SECTION */}
                  {hasInstallments ? (
                    <div className="rounded-xl border border-[#E9E9F0] bg-white p-6">
                      <div className="text-sm text-[#572575] font-semibold mb-6">
                        Flexible Payment Plans Available
                      </div>

                      {/* DYNAMIC PROGRESS BAR */}
                      <ProgressBar installments={program.installments} />

                      {/* INSTALLMENTS */}
                      <div
                        className={`grid md:grid-cols-${program.installments.length} gap-6 text-sm`}
                      >
                        {program.installments.map((item) => {
                          const statusType =
                            item.status === "paid"
                              ? "success"
                              : item.status === "upcoming"
                                ? "warning"
                                : "neutral";

                          return (
                            <Installment
                              key={item.installment_no}
                              title={`Installment ${item.installment_no}`}
                              price={`${program.currency} ${item.amount}`}
                              note={item.description}
                              status={item.status}
                              statusType={statusType}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border bg-white p-4 text-sm text-[#572575] font-semibold">
                      No Flexible Payment Plans Available
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- PROGRESS BAR ---------------- */

const ProgressBar = ({ installments }) => {
  const total = installments.length;
  const paidCount = installments.filter((i) => i.status === "paid").length;

  const progressPercent = (paidCount / total) * 100;

  return (
    <div className="relative mb-10">
      {/* Base line */}
      <div className="absolute top-[14px] left-0 right-0 h-[6px] rounded-full bg-[#F5EFE6]" />

      {/* Completed line */}
      <div
        className="absolute top-[14px] left-0 h-[6px] rounded-full bg-[#3A8D0A] transition-all duration-500"
        style={{ width: `${progressPercent}%` }}
      />

      {/* Steps */}
      <div
        className="relative grid"
        style={{ gridTemplateColumns: `repeat(${total}, 1fr)` }}
      >
        {installments.map((item, index) => {
          let type = "locked";

          if (item.status === "paid") type = "completed";
          else if (index === paidCount) type = "warning";

          return (
            <div key={index} className="flex justify-start">
              <StepCircle type={type} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ---------------- SMALL COMPONENTS ---------------- */

const MetaItem = ({ icon, value, label }) => (
  <div className="flex items-center gap-2">
    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#F3E8FF]">
      {icon}
    </div>
    <div className="text-[12px] leading-tight">
      <p className="font-medium text-[#1C1C1C]">{value}</p>
      <p className="text-[#8A8A8A]">{label}</p>
    </div>
  </div>
);

const StepCircle = ({ type }) => {
  const styles = {
    completed: "bg-[#3A8D0A] text-white",
    warning: "bg-[#F29900] text-white",
    locked: "bg-[#E0E0E0] text-[#616161]",
  };

  const icons = {
    completed: <Check size={14} />,
    warning: <Clock size={14} />,
    locked: <Lock size={14} />,
  };

  return (
    <div
      className={`z-10 flex h-7 w-7 items-center justify-center rounded-full ${styles[type]}`}
    >
      {icons[type]}
    </div>
  );
};

const Installment = ({ title, price, note, status, statusType }) => {
  const badgeStyles = {
    success: "bg-[#E4FEDA] text-[#2D7D11] border border-[#A3D9A5]",
    warning: "bg-[#FFF3E0] text-[#F29900] border border-[#FFD8A8]",
    neutral: "bg-[#EEEEEE] text-[#616161] border border-[#BDBDBD]",
  };

  const iconMap = {
    success: <Check size={14} />,
    warning: <Clock size={14} />,
    neutral: <Lock size={14} />,
  };

  return (
    <div className="space-y-2">
      <p className="font-medium text-[#1C1C1C]">{title}</p>
      <p className="text-[16px] font-bold text-[#572575]">{price}</p>
      <p className="text-xs text-[#5F6368]">{note}</p>

      <span
        className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium ${badgeStyles[statusType]}`}
      >
        {iconMap[statusType]}
        {status}
      </span>
    </div>
  );
};
